import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import {
  ArrowRightCircleFill,
  CheckCircleFill,
  HandThumbsDownFill,
  HandThumbsUpFill,
  PlusCircleFill,
  Search,
  TrashFill,
  XCircleFill,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'

import { CustomButton } from '../../../../CustomButton'
import { CustomSpinner } from '../../../../CustomSpinner'

import ProdutosFilhosService from '../../../../../services/database/ProdutosFilhosService'
import ProdutosService from '../../../../../services/database/ProdutosService'

import { sendErrorApiMessage } from '../../../../../utils/sendErrorApiMessage'

import Swal from 'sweetalert2'
import {
  Article,
  Center,
  ContainerComponent,
  ContentComponent,
  FooterComponent,
  NextButton,
  StyledSearch,
} from '../../styles'

export const Filhos = forwardRef(
  ({ isVisible, onNext, product: { IDPRODUTOPAI, SKU_PRODUTO } }, ref) => {
    const [children, setChildren] = useState([])
    const [child, setChild] = useState({
      DESCRICAO: '',
    })
    const [isLoadingChildren, setIsLoadingChildren] = useState(true)

    const [resultsChildren, setResultsChildren] = useState([])
    const [isSearchChildrenLoading, setIsSearchChildrenLoading] =
      useState(false)

    const [isDeleteChildLoading, setIsDeleteFilhoLoading] = useState({})
    const [isUpdateChildLoading, setIsUpdateChildLoading] = useState({})

    const loadChildren = useCallback(async () => {
      try {
        setIsLoadingChildren(true)

        const items =
          await ProdutosFilhosService.getItemsByProductSku(SKU_PRODUTO)

        setChildren((prevState) => [
          ...items,
          ...prevState.filter((child) => child.toAdd),
        ])
      } catch (err) {
        sendErrorApiMessage(err, 'Não foi possível carregar os filhos!')
      } finally {
        setIsLoadingChildren(false)
      }
    }, [SKU_PRODUTO])

    function handleAddChild(filho) {
      function validateFields() {
        if (!filho.DESCRICAO) {
          return false
        }

        return true
      }

      if (validateFields()) {
        if (!filho.SKUPRODFILHO) {
          return
        }
      }

      const index = children.findIndex((c) => c.ID_PRODUTO === filho.ID_PRODUTO)

      if (index === -1) {
        setChildren((prevState) => [
          ...prevState,
          {
            ...filho,
            toAdd: true,
          },
        ])
      }
    }

    async function handleUpdateSaleChild({ child, index }, liberacaoVenda) {
      if (child.toAdd) {
        const index = children.findIndex(
          (c) => c.ID_PRODUTO === child.ID_PRODUTO,
        )

        setChildren((prevState) =>
          prevState.with(index, {
            ...prevState[index],
            LIBERADOVENDA: liberacaoVenda,
          }),
        )
      } else {
        setIsUpdateChildLoading((prevState) => ({
          ...prevState,
          [index]: true,
        }))

        try {
          await ProdutosFilhosService.updateLiberadoVenda(
            child.ID,
            liberacaoVenda,
            localStorage.idusuario,
          )

          toast.success('Liberação da venda atualizada!')
          loadChildren()
        } catch (err) {
          sendErrorApiMessage(err, 'Erro ao atualizar a liberação de venda!')
        } finally {
          setIsUpdateChildLoading((prevState) => ({
            ...prevState,
            [index]: false,
          }))
        }
      }
    }

    async function handleDeleteChild({ child, index }) {
      if (child.toAdd) {
        setChildren((prevState) =>
          prevState.filter((c) => c.ID_PRODUTO !== child.ID_PRODUTO),
        )
      } else {
        setIsDeleteFilhoLoading((prevState) => ({
          ...prevState,
          [index]: true,
        }))

        try {
          await ProdutosFilhosService.delete(child.ID, localStorage.idusuario)
          toast.success('Produto Filho deletado com sucesso!')

          loadChildren()
        } catch (err) {
          sendErrorApiMessage(err, 'Erro ao deletar o produto filho!')
        } finally {
          setIsDeleteFilhoLoading((prevState) => ({
            ...prevState,
            [index]: false,
          }))
        }
      }
    }

    const handleSearchProdutoFilho = useCallback(
      async (e) => {
        e.preventDefault()

        if (child.DESCRICAO.length === 0) {
          return
        }

        setIsSearchChildrenLoading(true)

        try {
          const results = await ProdutosService.getAll(child.DESCRICAO)

          setResultsChildren(!Array.isArray(results) ? [results] : results)
        } catch (err) {
          sendErrorApiMessage(err, 'Não foi possível pesquisar o produto!')
        } finally {
          setIsSearchChildrenLoading(false)
        }
      },
      [child.DESCRICAO],
    )

    function handleSelectProdutoFilho(child) {
      if (child?.SKU_PRINCIPAL) {
        Swal.fire({
          title:
            'Este produto já possui um produto pai, tem certeza que deseja selecionar?',
          showDenyButton: true,
          denyButtonText: `Não`,
          confirmButtonText: 'Sim',
        }).then((result) => {
          if (result.isConfirmed) {
            setChild({
              ID_PRODUTO: child.CODPRODUTO,
              SKUPRODFILHO: child.SKU,
              DESCRICAO: child.DESCRICAO,
              IDPRODUTOPAI,
              SKU_PRODUTO,
              LIBERADOVENDA: 'N',
            })
          } else {
          }

          setResultsChildren([])
        })
      } else {
        setChild({
          ID_PRODUTO: child.CODPRODUTO,
          SKUPRODFILHO: child.SKU,
          DESCRICAO: child.DESCRICAO,
          IDPRODUTOPAI,
          SKU_PRODUTO,
          LIBERADOVENDA: 'N',
        })

        setResultsChildren([])
      }
    }

    useEffect(() => {
      if (isVisible) {
        setChild({
          DESCRICAO: '',
        })
        setResultsChildren([])
        loadChildren()
      }
    }, [isVisible, loadChildren])

    useImperativeHandle(
      ref,
      () => ({
        getChildren: () => {
          return children
        },
      }),
      [children],
    )

    if (!isVisible) {
      return null
    }

    return (
      <ContainerComponent ref={ref}>
        <div>
          <ContentComponent>
            {isLoadingChildren ? (
              <Center
                style={{
                  justifyContent: 'flex-start',
                }}
              >
                <CustomSpinner />
                <p>Carregando Filhos...</p>
              </Center>
            ) : (
              <>
                <h4>Filhos</h4>

                <Row className="mb-3">
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>SKU ou Descrição do Produto</Form.Label>

                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                        }}
                      >
                        <Form.Control
                          value={child.DESCRICAO}
                          placeholder="Pesquise e aperte ENTER"
                          onChange={(e) => {
                            setChild((prevState) => ({
                              ...prevState,
                              DESCRICAO: e.target.value,
                            }))

                            if (e.target.value.length === 0) {
                              setResultsChildren([])
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.code === 'Enter' ||
                              e.code === 'NumpadEnter'
                            ) {
                              handleSearchProdutoFilho(e)
                            }
                          }}
                        />

                        <CustomButton
                          noMargin
                          color="#0d6efd"
                          onClick={handleSearchProdutoFilho}
                          isLoading={isSearchChildrenLoading}
                        >
                          <Search />
                        </CustomButton>

                        <CustomButton
                          noMargin
                          isDisabled={!child.IDPRODUTOPAI}
                          onClick={() => {
                            handleAddChild(child)

                            setChild({
                              DESCRICAO: '',
                            })
                          }}
                          color="#0d6efd"
                        >
                          <PlusCircleFill />
                        </CustomButton>
                      </div>
                    </Form.Group>

                    {isSearchChildrenLoading ? (
                      <div id="center-content">
                        <CustomSpinner size={40} />
                        <p>Carregando...</p>
                      </div>
                    ) : (
                      <>
                        {resultsChildren.length > 0 && (
                          <StyledSearch>
                            {resultsChildren.map((result) => (
                              <Article
                                key={result.CODPRODUTO}
                                onClick={() => handleSelectProdutoFilho(result)}
                              >
                                <p>
                                  <strong>{result.SKU}</strong> |{' '}
                                  {result.DESCRICAO}
                                </p>
                              </Article>
                            ))}
                          </StyledSearch>
                        )}
                      </>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col>
                    {children.length > 0 && (
                      <Table
                        striped
                        bordered
                        hover
                        responsive
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                width: 50,
                              }}
                            >
                              STATUS
                            </th>
                            <th
                              style={{
                                width: 50,
                              }}
                            >
                              SKU
                            </th>

                            <th>DESCRIÇÃO DO PRODUTO</th>

                            <th
                              style={{
                                width: 50,
                              }}
                            >
                              QTD
                            </th>

                            <th
                              style={{
                                width: 50,
                              }}
                            >
                              LIBERADO
                            </th>

                            <th
                              style={{
                                width: 50,
                              }}
                            >
                              AÇÕES
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {children.map((child, index) => (
                            <tr key={child.ID_PRODUTO}>
                              <td>
                                {child.toAdd ? (
                                  <OverlayTrigger
                                    overlay={
                                      <Tooltip>Salvo na memória</Tooltip>
                                    }
                                  >
                                    <XCircleFill
                                      color="#ea1010"
                                      size={20}
                                    />
                                  </OverlayTrigger>
                                ) : (
                                  <OverlayTrigger
                                    overlay={
                                      <Tooltip>Salvo no banco de dados</Tooltip>
                                    }
                                  >
                                    <CheckCircleFill
                                      color="#04821d"
                                      size={20}
                                    />
                                  </OverlayTrigger>
                                )}
                              </td>
                              <td>{child.SKUPRODFILHO}</td>
                              <td>{child.DESCRICAO}</td>
                              <td>{child.QTESTGER}</td>
                              <td>{child.LIBERADOVENDA}</td>
                              <td
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CustomButton
                                  isLoading={isDeleteChildLoading[index]}
                                  color="#ea1010"
                                  onClick={() =>
                                    handleDeleteChild({ child, index })
                                  }
                                >
                                  <TrashFill />
                                </CustomButton>

                                <CustomButton
                                  isLoading={isUpdateChildLoading[index]}
                                  color="#04821d"
                                  onClick={() =>
                                    handleUpdateSaleChild({ child, index }, 'S')
                                  }
                                >
                                  <HandThumbsUpFill />
                                </CustomButton>

                                <CustomButton
                                  isLoading={isUpdateChildLoading[index]}
                                  color="#ea1010"
                                  onClick={() =>
                                    handleUpdateSaleChild({ child, index }, 'N')
                                  }
                                >
                                  <HandThumbsDownFill />
                                </CustomButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Col>
                </Row>
              </>
            )}
          </ContentComponent>

          {!isLoadingChildren && (
            <FooterComponent>
              <OverlayTrigger overlay={<Tooltip>Avançar</Tooltip>}>
                <NextButton onClick={onNext}>
                  <ArrowRightCircleFill
                    size={15}
                    color="white"
                  />
                </NextButton>
              </OverlayTrigger>
            </FooterComponent>
          )}
        </div>
      </ContainerComponent>
    )
  },
)
